import ErrorPage from 'yoda-app-shell/lib/components/ErrorFullPage/ErrorFullPageLazy';
import React, { useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { array, bool, object, oneOfType, string } from 'prop-types';
import { useLocation } from 'react-router-dom';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { CanonincalFallback } from 'yoda-site-components/lib/components/SeoMetaTags/CanonicalFallback';
import { constructSEODefaultCanonicalUrl } from 'yoda-site-components/lib/helpers/Utils/seoHelper';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import CertonaConnected from '../Certona/CertonaLazy';
import FilterContent from '../FilterContent/FilterContent';
import FilterHeaderConnected from '../FilterHeader/FilterHeader';
import LoadPaginationConnected from '../Pagination/LoadPaginationLazy';
import NoGalleryResultsConnected from '../NoGalleryResults/NoGalleryResultsLazy';
import NoResultsCardConnected from '../NoResultsCard/NoResultsCard';
import NoSearchResults from '../NoSearchResults/NoSearchResultsLazy';
import ProductListConnected from '../Product/ProductList';
import SelectedFacetsConnected from '../SelectedFacets/SelectedFacets';
// import SuggestedFiltersConnected from '../SuggestedFilters/SuggestedFilters';
import selectNoSearchResults from '../../selectors/NoSearchResults';
import { getGalleryGridContentWithIris } from '../../actions/GalleryIrisAction';
import { updateUrl, showHorizonalFilters, getPageType, getURLParameterBykey } from '../../utils';
import ScrollRendererWrapper from '../ScrollRendererWrapper/ScrollRendererWrapper';

export const GalleryGrid = ({
    gridAds,
    irisComponents,
    irisComponentsWithoutGrid,
    irisPropsData,
    marketingSlot,
    isFromFailOverLayOut,
}) => {
    /* istanbul ignore next */
    const { pathname = '', search = '' } = useLocation();
    const dispatch = useDispatch();
    const marketingSlotValue = useRef(marketingSlot);
    const state = useSelector((reduxState) => reduxState);
    /* istanbul ignore next */
    const {
        context: {
            isPreview = false,
            featureFlags: { enableMultipleThumbnails = false } = {},
        } = {},
        multiSelectFilter: { enableProductsLoader = false } = {},
        failOverLayoutInfo: { isUpdateCanonicalUrl = true } = {},
        productList: { galleryDetails: { hasMultipleThumbnails = false } = {} } = {},
    } = state;
    const pagination = useSelector((state) => state.pagination.pagination);

    /* istanbul ignore next */
    const onPageChange = useCallback(
        (pageNumber, mktTiles, hasMarketingTiles) => {
            // TOFP2P-3609: CRITEO CHANGES add pagination to local storage
            LocalStorage.setData('paginationClick', true);
            const prevPageMktTiles = LocalStorage.getData('prevPageMktTiles');

            /* istanbul ignore next */
            if (pageNumber === null) {
                updateUrl(null, { page: pageNumber, mktTiles: null });
            } else if (
                (!enableMultipleThumbnails && hasMarketingTiles) ||
                (enableMultipleThumbnails && !hasMultipleThumbnails && hasMarketingTiles)
            ) {
                updateUrl(null, { page: pageNumber, mktTiles });
            } else if (
                enableMultipleThumbnails &&
                pagination?.multipleThumbNail &&
                pagination?.nextPageMktTiles > 0
            ) {
                if (pagination?.selectedPageNumber < pageNumber) {
                    LocalStorage.setData('prevPageMktTiles', getURLParameterBykey('mktTiles'));
                    updateUrl(null, {
                        page: pageNumber,
                        mktTiles: pageNumber > 1 ? pagination?.nextPageMktTiles : null,
                    });
                } else if (pagination?.selectedPageNumber > pageNumber) {
                    updateUrl(null, {
                        page: pageNumber,
                        mktTiles: pageNumber > 1 ? prevPageMktTiles : null,
                    });
                    LocalStorage.removeData('prevPageMktTiles');
                }
            } else {
                updateUrl(null, { page: pageNumber });
            }
        },
        [pagination, hasMultipleThumbnails]
    );
    /* istanbul ignore next */
    const displayMedaliaSurvey = useCallback(() => {
        const {
            context: {
                featureFlags: {
                    isDisplayPLPMedaliaSurvey = false,
                    isDisplaySRPMedaliaSurvey = false,
                } = {},
            } = {},
        } = state;
        const isSRP =
            (irisPropsData?.pageType === 'Search' || getPageType(pathname) === 's') &&
            isDisplaySRPMedaliaSurvey;
        // added !__SERVER_ condition on serverside srp div id is replacing with plp div id
        if ((isSRP || isDisplayPLPMedaliaSurvey) && !__SERVER__) {
            return (
                <div
                    id={isSRP ? 'srp_survey_inline' : 'plp_survey_inline'}
                    className={dt(['py-2', 'pl-2', 'relative', 'w-full'])}
                    data-automation-id={isSRP ? 'srp_survey_link' : 'plp_survey_link'}
                />
            );
        }
        return '';
    }, [irisPropsData, __SERVER__]);

    const getMarketingSlot = useCallback(() => {
        let marketingSlotData = [];

        if (marketingSlot?.content?.items) {
            marketingSlotData = marketingSlot?.content?.items?.filter((item) => item)?.slice(0, 2);
        }

        return marketingSlotData;
    }, [marketingSlot]);
    /* istanbul ignore next */
    const setGalleyMarketingTilesData = useCallback(() => {
        const {
            context: {
                featureFlags: {
                    disableOrphanProducts = false,
                    enableVisualRelatedSearch = false,
                    enableDynamicVisNav = false,
                } = {},
            } = {},
        } = state;

        const marketingSlotForIris = getMarketingSlot();

        dispatch(
            getGalleryGridContentWithIris({
                marketingSlot: marketingSlotForIris,
                disableOrphanProducts,
                enableVisualRelatedSearch,
                enableDynamicVisNav,
            })
        );
    }, [marketingSlot]);

    const renderProductList = useCallback(() => {
        const productlistclass = enableProductsLoader ? dt(['relative']) : '';
        return (
            <div id="gallery-product-list" className={productlistclass}>
                <ProductListConnected
                    gridAds={gridAds}
                    irisComponents={irisComponents}
                    irisComponentsWithoutGrid={irisComponentsWithoutGrid}
                    irisPropsData={irisPropsData}
                    marketingSlot={getMarketingSlot()}
                    productList={{}}
                />
            </div>
        );
    }, [enableProductsLoader, gridAds, irisComponents, irisComponentsWithoutGrid, irisPropsData]);
    /* istanbul ignore next */
    const constructCanonicalUrl = useCallback(() => {
        const {
            context: {
                featureFlags: { enableCanonicalFallback = false } = {},
                preferences: {
                    seoConfig: { excludeQueryParams = [], includeQueryParams = [] } = {},
                } = {},
            } = {},
        } = state;
        if (
            enableCanonicalFallback &&
            isFromFailOverLayOut &&
            isUpdateCanonicalUrl &&
            excludeQueryParams.length !== 0
        ) {
            let updatedPrams = '';
            const hostname = 'www.jcpenney.com';
            updatedPrams = constructSEODefaultCanonicalUrl(
                search,
                excludeQueryParams,
                includeQueryParams,
                false
            );
            return (
                <CanonincalFallback
                    urlLocation={pathname}
                    urlParams={updatedPrams}
                    hostname={hostname}
                />
            );
        }
        return '';
    }, [isFromFailOverLayOut, isUpdateCanonicalUrl, pathname]);
    /* istanbul ignore next */
    const renderFilters = useCallback(
        (showHorizontal) =>
            showHorizontal ? (
                <SelectedFacetsConnected
                    wrapperClass={dt([
                        'align-center',
                        'flex',
                        'flex-no-wrap',
                        'mb-2',
                        'overflow-y-auto',
                        'whitespace-nowrap',
                    ])}
                />
            ) : (
                <FilterHeaderConnected />
            ),
        [pathname]
    );
    /* istanbul ignore next */
    const getGalleryGridLayout = () => {
        const {
            statusCheck,
            context: {
                abTestEngagements = '',
                deviceType = {},
                deviceType: { isMobile },
                featureFlags: { enableStickyFacet = false } = {},
            } = {},
        } = state;
        const noResults = selectNoSearchResults(state);
        const isSearchPage = irisPropsData?.pageType === 'Search' || getPageType() === 's';
        const showHorizontal = showHorizonalFilters(abTestEngagements, deviceType);
        const enableStickyStyles = enableStickyFacet ? dt(['md:sticky', 'md:pl-[300px]']) : '';
        /*
         * __IS_MOBILE__ would be converted to props.deviceType.isMobile on server because of server's dynamic nature
         * so, we would need a Terser Plugin with mangle.properties options with property reserved=['props']
         * saying that it would mean that props constant would never be mangled allowing us to rely on a props constant in variable scope
         * which would be governed by our lint rule saying that if __IS_MOBILE__ is used there should be a props constant defined in scope
         *
         */
        /* istanbul ignore next */
        if (isSearchPage && statusCheck === 403) {
            return <ErrorPage templateType="404" />;
        }

        /* istanbul ignore next */
        if (noResults) {
            return (
                <ScrollRendererWrapper>
                    {isSearchPage ? <NoSearchResults /> : <NoGalleryResultsConnected />}
                    {!isSearchPage && displayMedaliaSurvey()}
                </ScrollRendererWrapper>
            );
        }

        const renderFiltersSection = (
            <>
                {/* <SuggestedFiltersConnected /> */}
                {isMobile ? renderFilters(showHorizontal) : <FilterHeaderConnected />}
            </>
        );

        const renderContentSection = (
            /* istanbul ignore next */
            <section
                className={`${dt([
                    'md:pr-0',
                    'lg:pl-1',
                    'md:min-w-0',
                    'md:flex-[3_0]',
                ])} ${enableStickyStyles} product-pane`}
                id="productlistheight"
            >
                <NoResultsCardConnected />
                {renderProductList()}
                <LoadPaginationConnected onPageChange={onPageChange} />
            </section>
        );

        /* istanbul ignore next */
        return (
            <>
                {renderFiltersSection}
                {isMobile ? (
                    renderContentSection
                ) : (
                    <div className={dt(['flex', 'flex-row'])}>
                        <FilterContent />
                        {renderContentSection}
                    </div>
                )}
                <ScrollRendererWrapper>
                    {displayMedaliaSurvey()}
                    <CertonaConnected page={isSearchPage ? 'searchRecZones' : 'galleryRecZones'} />
                </ScrollRendererWrapper>
            </>
        );
    };

    useEffect(() => {
        setGalleyMarketingTilesData();
    }, []);

    useEffect(() => {
        if (!(marketingSlot === marketingSlotValue?.current)) {
            setGalleyMarketingTilesData();
        }
    }, [marketingSlot]);

    let showContent = isPreview ? null : getGalleryGridLayout();
    if (!__SERVER__ && isPreview) showContent = getGalleryGridLayout();
    return (
        <>
            {constructCanonicalUrl()}
            {showContent}
        </>
    );
};

GalleryGrid.propTypes = {
    gridAds: oneOfType([object, string]),
    irisComponents: oneOfType([array, object]),
    irisComponentsWithoutGrid: oneOfType([array, object]),
    irisPropsData: oneOfType([array, object]),
    marketingSlot: oneOfType([array, object]),
    isFromFailOverLayOut: bool,
};

GalleryGrid.defaultProps = {
    gridAds: {},
    irisComponents: {},
    irisComponentsWithoutGrid: {},
    irisPropsData: {},
    marketingSlot: [],
    isFromFailOverLayOut: false,
};

export default GalleryGrid;
